/* 75-RC Brand Scan — custom element. Re-initialises automatically when the
   theme editor re-renders the section (connected/disconnected callbacks). */
(() => {
  if (customElements.get('brand-scan')) return;

  const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

  class BrandScan extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-stage]');
      this.line = this.querySelector('[data-scan-line]');
      this.items = Array.from(this.querySelectorAll('[data-name]'));
      if (!this.stage) return;

      this.scanEnabled = this.dataset.scan !== 'false';
      this.duration = Math.max(0.5, parseFloat(this.dataset.duration) || 5) * 1000;
      this.delay = Math.max(0, parseFloat(this.dataset.delay) || 0) * 1000;
      this.trigger = this.dataset.start || 'visible';
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.active = null;
      this.raf = 0;
      this.t0 = null;
      this.started = false;
      this.queue = [];
      this.ys = new Map();

      this.tick = this.tick.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onDocClick = this.onDocClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onPointerOver = this.onPointerOver.bind(this);
      this.onPointerOut = this.onPointerOut.bind(this);
      this.onMotionChange = this.onMotionChange.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('pointerover', this.onPointerOver);
      this.addEventListener('pointerdown', (e) => { this.lastPointer = e.pointerType; });
      this.addEventListener('pointerout', this.onPointerOut);
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKeyDown);
      if (this.motion.addEventListener) this.motion.addEventListener('change', this.onMotionChange);

      this.ro = new ResizeObserver(() => {
        this.setupVideo();
        this.layout();
        if (this.active) this.position(this.active.trigger, this.active.pop);
      });
      this.ro.observe(this.stage);

      this.setupVideo();
      this.layout();

      if (!this.scanEnabled || this.motion.matches) this.finish();
      else this.arm();

      if (window.Shopify && window.Shopify.designMode) this.bindEditor();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKeyDown);
      if (this.motion && this.motion.removeEventListener) this.motion.removeEventListener('change', this.onMotionChange);
      if (this.ro) this.ro.disconnect();
      if (this.io) this.io.disconnect();
      if (this.editorHandlers) {
        this.editorHandlers.forEach(([type, fn]) => document.removeEventListener(type, fn));
      }
    }

    /* ---------- Layout: keep names inside the stage, record their y ---------- */
    layout() {
      const W = this.stage.clientWidth;
      const H = this.stage.clientHeight;
      const g = Math.min(12, W * 0.03);
      this.H = H;

      this.items.forEach((el) => {
        el.style.setProperty('--nx', '0px');
        el.style.setProperty('--ny', '0px');
        const w = el.offsetWidth;
        const h = el.offsetHeight;
        const f = el.dataset.anchor === 'center' ? 0.5 : el.dataset.anchor === 'right' ? 1 : 0;
        const left = el.offsetLeft - w * f;
        const top = el.offsetTop - h / 2;

        let nx = 0;
        let ny = 0;
        if (left + w > W - g) nx = W - g - (left + w);
        if (left + nx < g) nx = g - left;
        if (top + h > H - g) ny = H - g - (top + h);
        if (top + ny < g) ny = g - top;

        if (nx) el.style.setProperty('--nx', `${nx}px`);
        if (ny) el.style.setProperty('--ny', `${ny}px`);
        this.ys.set(el, top + ny + h / 2);
      });

      this.queue = this.items
        .filter((el) => !el.classList.contains('is-revealed'))
        .sort((a, b) => this.ys.get(a) - this.ys.get(b));
    }

    /* ---------- Scan ---------- */
    arm() {
      if (this.trigger === 'load') {
        this.start();
        return;
      }
      this.io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this.io.disconnect();
            this.start();
          }
        },
        { threshold: 0, rootMargin: '0px 0px -15% 0px' }
      );
      this.io.observe(this.stage);
    }

    start() {
      if (this.started) return;
      this.started = true;
      this.t0 = null;
      this.line.classList.remove('is-done');
      this.line.style.transform = 'translate3d(0, 0, 0)';
      this.classList.add('brand-scan--scanning');
      this.raf = requestAnimationFrame(this.tick);
    }

    tick(ts) {
      if (this.t0 === null) this.t0 = ts + this.delay;
      const p = clamp((ts - this.t0) / this.duration, 0, 1);
      const dpr = window.devicePixelRatio || 1;
      const y = Math.round(p * this.H * dpr) / dpr; // snap to device pixels = sharp line
      this.line.style.transform = `translate3d(0, ${y}px, 0)`;

      while (this.queue.length && this.ys.get(this.queue[0]) <= y) {
        this.queue.shift().classList.add('is-revealed');
      }

      if (p < 1) this.raf = requestAnimationFrame(this.tick);
      else this.finish();
    }

    finish() {
      cancelAnimationFrame(this.raf);
      this.started = true;
      if (this.io) this.io.disconnect();
      this.items.forEach((el) => el.classList.add('is-revealed'));
      this.queue = [];
      this.classList.remove('brand-scan--scanning');
      if (this.line) this.line.classList.add('is-done');
    }

    replay() {
      if (!this.scanEnabled || this.motion.matches) {
        this.finish();
        return;
      }
      cancelAnimationFrame(this.raf);
      this.close();
      this.started = false;
      this.items.forEach((el) => el.classList.remove('is-revealed'));
      this.layout();
      this.start();
    }

    onMotionChange() {
      if (this.motion.matches) this.finish();
      this.setupVideo();
    }

    setupVideo() {
      this.querySelectorAll('video').forEach((v) => {
        v.muted = true;
        v.playsInline = true;
        // Only the visible one plays (desktop vs mobile video)
        if (this.motion.matches || !v.offsetParent) {
          v.removeAttribute('autoplay');
          v.pause();
        } else if (v.paused) {
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        }
      });
    }

    /* ---------- Hover image ---------- */
    // Mouse/pen: show while hovering the name
    onPointerOver(e) {
      if (e.pointerType === 'touch') return;
      const trig = e.target.closest('[data-trigger]');
      if (trig && this.contains(trig) && (!this.active || this.active.trigger !== trig)) this.open(trig);
    }

    onPointerOut(e) {
      if (e.pointerType === 'touch' || !this.active) return;
      const trig = e.target.closest('[data-trigger]');
      if (trig === this.active.trigger && !trig.contains(e.relatedTarget)) this.close();
    }

    // Touch: tap a name to show, tap it again or anywhere else to hide
    onClick(e) {
      const trig = e.target.closest('[data-trigger]');
      if (!trig || !this.contains(trig)) return;
      if (this.lastPointer !== 'touch') return;
      if (this.active && this.active.trigger === trig) this.close();
      else this.open(trig);
    }

    onDocClick(e) {
      if (!this.active) return;
      if (e.target.closest && e.target.closest('[data-trigger]') && this.contains(e.target)) return;
      this.close();
    }

    onKeyDown(e) {
      if (e.key === 'Escape' && this.active) this.close();
    }

    // Keyboard: focusing a name shows its image (and reveals the name if the scan hasn't reached it)
    onFocusIn(e) {
      const item = e.target.closest('[data-name]');
      if (item) item.classList.add('is-revealed');
      const trig = e.target.closest('[data-trigger]');
      if (trig && this.contains(trig) && trig.matches(':focus-visible')) this.open(trig);
    }

    onFocusOut(e) {
      if (this.active && e.target === this.active.trigger) this.close();
    }

    open(trigger) {
      const pop = document.getElementById(trigger.getAttribute('aria-describedby'));
      if (!pop) return;
      if (this.active) this.close();

      const item = trigger.closest('[data-name]');
      if (item) item.classList.add('is-revealed');

      this.position(trigger, pop);
      pop.classList.add('is-open');
      trigger.classList.add('is-active');
      this.active = { trigger, pop };

      const img = pop.querySelector('img');
      if (img && !img.complete) {
        img.addEventListener('load', () => {
          if (this.active && this.active.pop === pop) this.position(trigger, pop);
        }, { once: true });
      }
    }

    close() {
      if (!this.active) return;
      const { trigger, pop } = this.active;
      pop.classList.remove('is-open');
      trigger.classList.remove('is-active');
      this.active = null;
    }

    // Place the image beside the name, always fully inside the stage
    position(trigger, pop) {
      const s = this.stage.getBoundingClientRect();
      const r = trigger.getBoundingClientRect();
      const W = s.width;
      const H = s.height;
      const g = Math.min(12, W * 0.03);
      const gap = 10;

      pop.style.setProperty('--pop-max-h', `${Math.max(120, H - 2 * g)}px`);
      pop.style.left = '0px';
      pop.style.top = '0px';
      const pw = pop.offsetWidth;
      let ph = pop.offsetHeight;

      const L = r.left - s.left;
      const R = r.right - s.left;
      const T = r.top - s.top;
      const B = r.bottom - s.top;
      const fitsR = R + gap + pw <= W - g;
      const fitsL = L - gap - pw >= g;
      const pref = trigger.dataset.side || 'auto';

      let side;
      if (pref === 'left') side = fitsL ? 'left' : fitsR ? 'right' : 'stack';
      else side = fitsR ? 'right' : fitsL ? 'left' : 'stack';

      let x;
      let y;
      if (side === 'right') {
        x = R + gap;
        y = T + r.height / 2 - ph / 2;
      } else if (side === 'left') {
        x = L - gap - pw;
        y = T + r.height / 2 - ph / 2;
      } else {
        // Not enough room beside it (narrow screens): use the larger space below or
        // above the name, shrinking the card so it never covers the name itself
        x = L + r.width / 2 - pw / 2;
        const below = H - g - (B + gap);
        const above = T - gap - g;
        const space = Math.max(below, above);
        if (ph > space && space >= 160) {
          pop.style.setProperty('--pop-max-h', `${space}px`);
          ph = pop.offsetHeight;
        }
        y = below >= above ? B + gap : T - gap - ph;
      }

      x = clamp(x, g, Math.max(g, W - g - pw));
      y = clamp(y, g, Math.max(g, H - g - ph));
      pop.style.left = `${Math.round(x)}px`;
      pop.style.top = `${Math.round(y)}px`;
    }

    /* ---------- Theme editor ---------- */
    bindEditor() {
      const onSectionSelect = (e) => {
        if (e.detail && e.detail.sectionId === this.dataset.sectionId) this.replay();
      };
      const onBlockSelect = (e) => {
        if (!this.contains(e.target)) return;
        this.finish();
        const t = e.target.querySelector('[data-trigger]');
        if (t) this.open(t);
      };
      const onBlockDeselect = (e) => {
        if (this.contains(e.target)) this.close();
      };
      this.editorHandlers = [
        ['shopify:section:select', onSectionSelect],
        ['shopify:block:select', onBlockSelect],
        ['shopify:block:deselect', onBlockDeselect],
      ];
      this.editorHandlers.forEach(([type, fn]) => document.addEventListener(type, fn));
    }
  }

  customElements.define('brand-scan', BrandScan);
})();
